import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  UserGroupIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService, PredictionService } from '../../../services/firebaseService';
import {
  getEliminationTypeDisplayLabel,
  isDoubleLikeEliminationWeek,
  isMultiEliminationWeek,
} from '../../../utils/predictionUtils';

export default function WeekPicks() {
  const { seasonId, weekNumber } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [seasonId, weekNumber]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        setError('Season not found');
        return;
      }
      setSeason(seasonData);
      
      // Find the specific week
      const weekData = seasonData.weeks?.find(w => w.weekNumber === parseInt(weekNumber));
      if (!weekData) {
        setError('Week not found');
        return;
      }
      setWeek(weekData);
      
      // Load contestants for this season
      const seasonContestants = await ContestantService.getContestantsBySeason(seasonId);
      setContestants(seasonContestants);
      
      // Load all predictions for this week across all leagues
      const allPredictions = await PredictionService.getPredictionsBySeasonAndWeek(seasonId, parseInt(weekNumber));
      setPredictions(allPredictions);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getContestantName = (contestantId) => {
    if (!contestantId) return 'N/A';
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? contestant.name : contestantId;
  };

  const getPredictionStats = () => {
    const stats = {};
    
    // Initialize all contestants with zero counts
    contestants.forEach(contestant => {
      stats[contestant.id] = { 
        name: contestant.name, 
        totalCount: 0,
        counts: {}
      };
    });
    
    predictions.forEach(prediction => {
      if (!prediction.predictions) return;
      
      // Handle different prediction formats
      if (week.isFinale) {
        // Finale: votedOut, fireMakingLoser, winner
        ['votedOut', 'fireMakingLoser', 'winner'].forEach(key => {
          const contestantId = prediction.predictions[key];
          if (contestantId && stats[contestantId]) {
            if (!stats[contestantId].counts[key]) {
              stats[contestantId].counts[key] = 0;
            }
            stats[contestantId].counts[key]++;
            stats[contestantId].totalCount++;
          }
        });
      } else if (isMultiEliminationWeek(week)) {
        const maxPos = week.eliminationType === 'triple' ? 3 : 2;
        const votedOut = prediction.predictions.votedOut;
        if (Array.isArray(votedOut) && votedOut.length > 0 && votedOut[0] && typeof votedOut[0] === 'object' && 'position' in votedOut[0]) {
          votedOut.forEach(item => {
            const contestantId = item?.contestantId;
            const pos = item?.position;
            if (contestantId && stats[contestantId] && pos >= 1 && pos <= maxPos) {
              const key = `votedOut${pos}`;
              if (!stats[contestantId].counts[key]) {
                stats[contestantId].counts[key] = 0;
              }
              stats[contestantId].counts[key]++;
              stats[contestantId].totalCount++;
            }
          });
        } else {
          const legacyKeys = ['votedOut1', 'votedOut2'];
          if (week.eliminationType === 'triple') legacyKeys.push('votedOut3');
          legacyKeys.forEach(key => {
            const contestantId = prediction.predictions[key];
            if (contestantId && stats[contestantId]) {
              if (!stats[contestantId].counts[key]) {
                stats[contestantId].counts[key] = 0;
              }
              stats[contestantId].counts[key]++;
              stats[contestantId].totalCount++;
            }
          });
        }
      } else {
        // Single elimination: votedOut
        const contestantId = prediction.predictions.votedOut;
        if (contestantId && stats[contestantId]) {
          if (!stats[contestantId].counts.votedOut) {
            stats[contestantId].counts.votedOut = 0;
          }
          stats[contestantId].counts.votedOut++;
          stats[contestantId].totalCount++;
        }
      }
    });
    
    return stats;
  };

  const getTotalPredictions = () => {
    return predictions.length;
  };

  const getWeekTypeLabel = () => {
    if (week.isFinale) return 'Finale';
    return getEliminationTypeDisplayLabel(week) || 'Single Elimination';
  };

  const getPredictionLabels = () => {
    if (week.isFinale) {
      return [
        { key: 'votedOut', label: 'Voted Out' },
        { key: 'fireMakingLoser', label: 'Fire-Making Loser' },
        { key: 'winner', label: 'Winner' }
      ];
    } else if (week.eliminationType === 'triple') {
      return [
        { key: 'votedOut1', label: 'Voted Out #1' },
        { key: 'votedOut2', label: 'Voted Out #2' },
        { key: 'votedOut3', label: 'Voted Out #3' },
      ];
    } else if (isDoubleLikeEliminationWeek(week)) {
      return [
        { key: 'votedOut1', label: 'Voted Out #1' },
        { key: 'votedOut2', label: 'Voted Out #2' },
      ];
    } else {
      return [
        { key: 'votedOut', label: 'Voted Out' }
      ];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/admin/seasons/${seasonId}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Season
          </button>
        </div>
      </div>
    );
  }

  const predictionStats = getPredictionStats();
  const totalPredictions = getTotalPredictions();
  const predictionLabels = getPredictionLabels();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/admin/seasons/${seasonId}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Week {weekNumber} - Prediction Picks
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {season.name} • {getWeekTypeLabel()} • {totalPredictions} total predictions
                </p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getWeekTypeLabel()}
                  </span>
                  {week.isMergeWeek && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Merge Week
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Prediction Statistics */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <ChartBarIcon className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Prediction Statistics</h2>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-2">
                {Object.entries(predictionStats)
                  .sort(([,a], [,b]) => b.totalCount - a.totalCount)
                  .map(([contestantId, data]) => {
                    const hasBreakdown =
                      isMultiEliminationWeek(week) &&
                      (data.counts.votedOut1 > 0 ||
                        data.counts.votedOut2 > 0 ||
                        data.counts.votedOut3 > 0);
                    return (
                      <div key={contestantId} className="border border-gray-200 rounded-lg p-3 bg-white hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900">{data.name}</h3>
                            <div className="text-xs text-gray-500 mt-1">
                              {data.totalCount} prediction{data.totalCount !== 1 ? 's' : ''}
                              {hasBreakdown && (
                                <span className="block mt-0.5 text-gray-400">
                                  {data.counts.votedOut1 || 0} as 1st, {data.counts.votedOut2 || 0} as 2nd
                                  {week?.eliminationType === 'triple' &&
                                    `, ${data.counts.votedOut3 || 0} as 3rd`}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-2 flex-shrink-0">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              data.totalCount > 0
                                ? 'bg-survivor-100 text-survivor-800'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {data.totalCount}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-6">
                  <span className="text-gray-600">
                    <span className="font-medium text-gray-900">{totalPredictions}</span> total predictions
                  </span>
                  <span className="text-gray-600">
                    <span className="font-medium text-gray-900">{Object.keys(predictionStats).length}</span> contestants
                  </span>
                  <span className="text-gray-600">
                    <span className="font-medium text-gray-900">{getWeekTypeLabel()}</span> week
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
